(() => {
  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const showToast = (message) => {
    const toast = document.querySelector('.eonix-toast');
    if (!toast) return;
    toast.textContent = message;
    toast.setAttribute('aria-hidden', 'false');
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timeout);
    showToast.timeout = window.setTimeout(() => {
      toast.classList.remove('is-visible');
      toast.setAttribute('aria-hidden', 'true');
    }, 2600);
  };

  const updateCartCount = async () => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('[data-cart-count]').forEach((node) => {
      node.textContent = cart.item_count;
    });
  };

  const initReveal = (root = document) => {
    const items = root.querySelectorAll('.reveal:not(.in)');
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      items.forEach((item) => item.classList.add('in'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((item) => observer.observe(item));
  };

  const initHeader = () => {
    const header = document.querySelector('.header');
    if (!header) return;
    const update = () => header.classList.toggle('stuck', window.scrollY > 12);
    update();
    window.addEventListener('scroll', update, { passive: true });
  };

  const initAbsorptionBars = (root = document) => {
    const cards = root.querySelectorAll('.abs-card:not([data-bars-ready])');
    if (!cards.length) return;
    if (!('IntersectionObserver' in window)) {
      cards.forEach((card) => card.querySelectorAll('.abs-fill').forEach((fill) => {
        fill.style.width = `${fill.dataset.w}%`;
      }));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.querySelectorAll('.abs-fill').forEach((fill) => {
          fill.style.width = `${fill.dataset.w}%`;
        });
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.3 });
    cards.forEach((card) => {
      card.dataset.barsReady = 'true';
      observer.observe(card);
    });
  };

  const initProductTabs = (root = document) => {
    root.querySelectorAll('[data-product-tabs]:not([data-tabs-ready])').forEach((tabs) => {
      tabs.dataset.tabsReady = 'true';
      const scope = tabs.closest('[data-product-catalog]');
      if (!scope) return;
      tabs.querySelectorAll('[data-filter]').forEach((tab) => {
        tab.addEventListener('click', () => {
          const filter = tab.dataset.filter;
          tabs.querySelectorAll('[data-filter]').forEach((item) => {
            const active = item === tab;
            item.classList.toggle('active', active);
            item.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          scope.querySelectorAll('[data-product-category]').forEach((card) => {
            card.hidden = filter !== 'all' && card.dataset.productCategory !== filter;
          });
        });
      });
    });
  };

  const initAjaxCart = (root = document) => {
    root.querySelectorAll('form[data-eonix-add]:not([data-cart-ready])').forEach((form) => {
      form.dataset.cartReady = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('[type="submit"]');
        if (!button || button.disabled) return;
        const original = button.innerHTML;
        button.disabled = true;
        button.textContent = 'Añadiendo…';
        try {
          const endpoint = form.action.replace(/\/cart\/add(?:\.js)?$/, '/cart/add.js');
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          const formData = new FormData(form);

          if (cart?.getSectionsToRender) {
            formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
            formData.append('sections_url', window.location.pathname);
            cart.setActiveElement?.(document.activeElement);
          }

          const response = await fetch(endpoint, {
            method: 'POST',
            body: formData,
            headers: { Accept: 'application/json' }
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || 'No se pudo añadir el producto.');
          if (cart?.renderContents && result.sections) {
            cart.renderContents(result);
          } else {
            await updateCartCount();
          }
          button.textContent = 'Añadido ✓';
          showToast('Producto añadido al carrito');
          window.setTimeout(() => { button.innerHTML = original; }, 1800);
        } catch (error) {
          button.innerHTML = original;
          showToast(error.message || 'Ha ocurrido un error.');
        } finally {
          button.disabled = false;
        }
      });
    });
  };

  const formatProductMoney = (cents, currency) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'es-ES', {
        style: 'currency',
        currency: currency || 'EUR'
      }).format(amount);
    } catch (_error) {
      return `${amount.toFixed(2).replace('.', ',')} €`;
    }
  };

  const initProductAccordions = (root = document) => {
    root.querySelectorAll('[data-pdp-accordion] .pdp-acc-q:not([data-accordion-ready])').forEach((button) => {
      button.dataset.accordionReady = 'true';
      button.addEventListener('click', () => {
        const item = button.closest('.pdp-acc-item');
        const answer = item?.querySelector('.pdp-acc-a');
        if (!item || !answer) return;
        const open = !item.classList.contains('open');
        item.classList.toggle('open', open);
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        answer.hidden = !open;
      });
    });
  };

  const initProductTimelines = (root = document) => {
    root.querySelectorAll('[data-pdp-timeline]:not([data-timeline-ready])').forEach((timeline) => {
      timeline.dataset.timelineReady = 'true';
      const section = timeline.closest('.pdp-timeline');
      if (!section) return;
      timeline.querySelectorAll('[data-timeline-tab]').forEach((tab) => {
        tab.addEventListener('click', () => {
          const id = tab.dataset.timelineTab;
          timeline.querySelectorAll('[data-timeline-tab]').forEach((item) => {
            const active = item === tab;
            item.classList.toggle('active', active);
            item.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          section.querySelectorAll('[data-timeline-panel]').forEach((panel) => {
            const active = panel.dataset.timelinePanel === id;
            panel.classList.toggle('active', active);
            panel.hidden = !active;
          });
        });
      });
    });
  };

  const initProductComparisonBars = (root = document) => {
    const cards = root.querySelectorAll('.pdp-comparison-card:not([data-comparison-ready])');
    if (!cards.length) return;
    const fill = (card) => card.querySelectorAll('[data-w]').forEach((bar) => {
      bar.style.width = `${Math.max(0, Math.min(100, Number(bar.dataset.w || 0)))}%`;
    });
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      cards.forEach(fill);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        fill(entry.target);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.3 });
    cards.forEach((card) => {
      card.dataset.comparisonReady = 'true';
      observer.observe(card);
    });
  };

  const initProductPage = (root = document) => {
    root.querySelectorAll('[data-eonix-pdp]:not([data-pdp-ready])').forEach((pdp) => {
      pdp.dataset.pdpReady = 'true';
      const variantsNode = pdp.querySelector('[data-pdp-variants]');
      const form = pdp.querySelector('[data-pdp-product-form]');
      if (!variantsNode || !form) return;

      let variants = [];
      try {
        variants = JSON.parse(variantsNode.textContent);
      } catch (_error) {
        return;
      }

      const currency = pdp.dataset.currency || 'EUR';
      const addLabel = pdp.dataset.addLabel || 'Añadir al carrito';
      const soldOutLabel = pdp.dataset.soldOutLabel || 'Agotado';
      const saleLabel = pdp.dataset.saleLabel || 'OFERTA';
      const variantInput = form.querySelector('[data-pdp-variant-id]');
      const sellingPlanInput = form.querySelector('[data-pdp-selling-plan]');
      const submit = form.querySelector('[data-pdp-submit]');
      const submitLabel = form.querySelector('[data-pdp-submit-label]');
      const stickySubmit = pdp.querySelector('[data-pdp-sticky-submit]');
      let currentVariant = variants.find((variant) => String(variant.id) === String(variantInput?.value)) || variants[0];
      let currentPlan = null;

      const showMedia = (mediaId) => {
        if (!mediaId) return;
        const media = pdp.querySelector(`[data-pdp-media="${mediaId}"]`);
        if (!media) return;
        pdp.querySelectorAll('[data-pdp-media]').forEach((item) => {
          const active = item === media;
          item.classList.toggle('active', active);
          item.hidden = !active;
          if (!active) item.querySelectorAll('video').forEach((video) => video.pause());
        });
        pdp.querySelectorAll('[data-pdp-thumb]').forEach((thumb) => {
          const active = thumb.dataset.pdpThumb === String(mediaId);
          thumb.classList.toggle('active', active);
          thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      };

      const adjustedPrice = (basePrice) => {
        if (!currentPlan) return Number(basePrice || 0);
        const type = currentPlan.dataset.adjustmentType;
        const value = Number(currentPlan.dataset.adjustmentValue || 0);
        if (type === 'percentage') return Math.round(basePrice * (100 - value) / 100);
        if (type === 'fixed_amount') return Math.max(0, basePrice - value);
        if (type === 'price' || type === 'fixed_price') return value;
        return Number(basePrice || 0);
      };

      const updatePrices = () => {
        if (!currentVariant) return;
        const price = adjustedPrice(currentVariant.price);
        const formatted = formatProductMoney(price, currency);
        pdp.querySelectorAll('[data-pdp-price],[data-pdp-atc-price],[data-pdp-sticky-price]').forEach((node) => {
          node.textContent = formatted;
        });
        pdp.querySelectorAll('[data-pdp-onetime-price]').forEach((node) => {
          node.textContent = formatProductMoney(currentVariant.price, currency);
        });

        const compare = pdp.querySelector('[data-pdp-compare-price]');
        const comparePrice = Math.max(
          Number(currentVariant.compare_at_price || 0),
          currentPlan ? Number(currentVariant.price || 0) : 0
        );
        if (compare) {
          const showCompare = comparePrice > price;
          compare.classList.toggle('is-hidden', !showCompare);
          compare.textContent = showCompare ? formatProductMoney(comparePrice, currency) : '';
        }

        const badge = pdp.querySelector('[data-pdp-sale-badge]');
        if (badge) {
          const percent = comparePrice > price ? Math.round((comparePrice - price) * 100 / comparePrice) : 0;
          badge.classList.toggle('is-hidden', percent < 1);
          if (percent > 0) badge.textContent = `-${percent}% ${saleLabel}`;
        }
      };

      const updateVariant = () => {
        const selectedOptions = Array.from(form.querySelectorAll('[data-pdp-option]')).map((group) => {
          return group.querySelector('[data-pdp-option-input]:checked')?.value;
        });
        if (selectedOptions.length) {
          currentVariant = variants.find((variant) => variant.options.every((value, index) => value === selectedOptions[index]));
        }
        if (!currentVariant) {
          if (submit) submit.disabled = true;
          if (stickySubmit) stickySubmit.disabled = true;
          if (submitLabel) submitLabel.textContent = 'No disponible';
          return;
        }

        if (variantInput) variantInput.value = currentVariant.id;
        const available = Boolean(currentVariant.available);
        if (submit) submit.disabled = !available;
        if (stickySubmit) {
          stickySubmit.disabled = !available;
          stickySubmit.textContent = available ? addLabel : soldOutLabel;
        }
        if (submitLabel) submitLabel.textContent = available ? addLabel : soldOutLabel;
        updatePrices();
        if (currentVariant.featured_media?.id) showMedia(currentVariant.featured_media.id);
        if (window.history?.replaceState && pdp.dataset.productUrl) {
          window.history.replaceState({}, '', `${pdp.dataset.productUrl}?variant=${currentVariant.id}`);
        }
      };

      pdp.querySelectorAll('[data-pdp-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => showMedia(thumb.dataset.pdpThumb));
      });

      pdp.querySelectorAll('[data-pdp-tabs] [data-pdp-tab]').forEach((tab) => {
        tab.addEventListener('click', () => {
          const target = tab.dataset.pdpTab;
          pdp.querySelectorAll('[data-pdp-tab]').forEach((item) => {
            const active = item === tab;
            item.classList.toggle('active', active);
            item.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          pdp.querySelectorAll('[data-pdp-panel]').forEach((panel) => {
            const active = panel.dataset.pdpPanel === target;
            panel.classList.toggle('active', active);
            panel.hidden = !active;
          });
        });
      });

      form.querySelectorAll('[data-pdp-option-input]').forEach((input) => {
        input.addEventListener('change', updateVariant);
      });

      form.querySelectorAll('[data-pdp-plan-choice]').forEach((choice) => {
        choice.addEventListener('change', () => {
          if (!choice.checked) return;
          currentPlan = choice.value ? choice : null;
          form.querySelectorAll('.pdp-purchase-card').forEach((card) => {
            card.classList.toggle('selected', card.contains(choice));
          });
          if (sellingPlanInput) {
            sellingPlanInput.value = choice.value;
            sellingPlanInput.disabled = !choice.value;
          }
          updatePrices();
        });
      });

      form.querySelectorAll('[data-quantity-change]').forEach((button) => {
        button.addEventListener('click', () => {
          const input = form.querySelector('[data-pdp-quantity] input');
          if (!input) return;
          const change = Number(button.dataset.quantityChange || 0);
          input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + change);
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      stickySubmit?.addEventListener('click', () => form.requestSubmit());
      updateVariant();
    });
  };

  const initialise = (root = document) => {
    initReveal(root);
    initAbsorptionBars(root);
    initProductTabs(root);
    initProductPage(root);
    initProductAccordions(root);
    initProductTimelines(root);
    initProductComparisonBars(root);
    initAjaxCart(root);
  };

  onReady(() => {
    initHeader();
    initialise(document);
  });

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
